import { useMemo } from 'react';

import { useMoplaLegsFeedbacks } from '@mopla/business-logic';
import { PassengerBookingView } from '@mopla/data-models';
import { getMoplaLegs } from '@mopla/utils';

import { buildRatableLegs, findActiveLeg } from './buildRatableLegs';
import { isBookingRatable } from './isBookingRatable';
import { IRatableLeg } from './types';

/** Per-leg rating state fetched from the feedbacks collection. */
export const useRatableLegs = (booking: PassengerBookingView) => {
	const moplaLegsIds = useMemo(
		() => getMoplaLegs(booking.itinerary.legs).map(({ legId }) => legId),
		[booking]
	);

	const { data: feedbackData, isError } = useMoplaLegsFeedbacks(
		moplaLegsIds,
		moplaLegsIds.length > 0
	);

	/** {feedbackData} may still hold a previous booking's legs. */
	const isReady = useMemo(
		() => moplaLegsIds.every((id) => feedbackData?.some((d) => d.legId === id)),
		[moplaLegsIds, feedbackData]
	);

	const legs = useMemo<IRatableLeg[]>(
		() =>
			buildRatableLegs(booking.itinerary.legs, (leg) => {
				const entry = feedbackData?.find((data) => data.legId === leg.legId);

				return {
					rating: entry?.feedback?.rating,
					driverName: entry?.driverCarDetails?.displayName,
				};
			}),
		[booking, feedbackData]
	);

	return {
		legs,
		activeLeg: findActiveLeg(legs),
		isWithinRatingWindow: isBookingRatable(booking),
		isLoading: !isReady && !isError,
		isError: Boolean(isError),
	};
};
